/**
 * Add Service Pricing Modal
 * Modal for adding/editing service pricing profiles
 */

import React, { useState } from 'react';
import { useAuthStore } from '../store/authStore';
import {
  saveServicePricing,
  calculateOptimalPrice,
  SERVICE_CATEGORIES,
  PRICING_STRATEGIES,
} from '../services/pricingIntelligenceService';
import { formatCurrency } from '../utils/formatters';
import './Modal.css';

const AddServicePricingModal = ({ onClose, onSuccess, editData = null }) => {
  const user = useAuthStore((state) => state.user);

  const [serviceName, setServiceName] = useState(editData?.serviceName || '');
  const [category, setCategory] = useState(editData?.category || '');
  const [description, setDescription] = useState(editData?.description || '');
  const [currentRate, setCurrentRate] = useState(editData?.currentRate || '');
  const [strategy, setStrategy] = useState(editData?.strategy || PRICING_STRATEGIES.HOURLY);
  const [targetMargin, setTargetMargin] = useState(editData?.targetMargin || 30);

  // Cost inputs
  const [directCosts, setDirectCosts] = useState(editData?.costs?.directCosts || '');
  const [indirectCosts, setIndirectCosts] = useState(editData?.costs?.indirectCosts || '');
  const [timeHours, setTimeHours] = useState(editData?.costs?.timeHours || '');
  const [desiredHourlyRate, setDesiredHourlyRate] = useState('');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);

  const calculateSuggestedRate = () => {
    const costs = {
      directCosts: parseFloat(directCosts) || 0,
      indirectCosts: parseFloat(indirectCosts) || 0,
      timeHours: parseFloat(timeHours) || 0,
      desiredHourlyRate: parseFloat(desiredHourlyRate) || 0,
    };

    return calculateOptimalPrice(costs, targetMargin, strategy);
  };

  const suggestedRate = showCalculator ? calculateSuggestedRate() : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!serviceName || !category || !currentRate) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const pricingData = {
        serviceName,
        category,
        description,
        currentRate: parseFloat(currentRate),
        suggestedRate: suggestedRate || parseFloat(currentRate),
        strategy,
        targetMargin,
        directCosts: parseFloat(directCosts) || 0,
        indirectCosts: parseFloat(indirectCosts) || 0,
        timeHours: parseFloat(timeHours) || 0,
      };

      await saveServicePricing(user.uid, pricingData);

      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editData ? 'Edit Service Pricing' : 'Add Service Pricing'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Basic Info */}
            <div className="form-group">
              <label htmlFor="serviceName">Service Name *</label>
              <input
                id="serviceName"
                type="text"
                className="input"
                placeholder="e.g., Website Development, SEO Consulting"
                value={serviceName}
                onChange={(e) => setServiceName(e.target.value)}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="category">Category *</label>
                <select
                  id="category"
                  className="input"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  required
                >
                  <option value="">Select category</option>
                  {SERVICE_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="strategy">Pricing Strategy</label>
                <select
                  id="strategy"
                  className="input"
                  value={strategy}
                  onChange={(e) => setStrategy(e.target.value)}
                >
                  <option value={PRICING_STRATEGIES.COST_PLUS}>Cost Plus</option>
                  <option value={PRICING_STRATEGIES.HOURLY}>Hourly Rate</option>
                  <option value={PRICING_STRATEGIES.PROJECT}>Fixed Project</option>
                  <option value={PRICING_STRATEGIES.VALUE_BASED}>Value Based</option>
                  <option value={PRICING_STRATEGIES.COMPETITIVE}>Competitive</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                className="input"
                rows="2"
                placeholder="Brief description of the service"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="currentRate">Current Rate * ({strategy === PRICING_STRATEGIES.HOURLY ? 'per hour' : 'per project'})</label>
                <input
                  id="currentRate"
                  type="number"
                  className="input"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  value={currentRate}
                  onChange={(e) => setCurrentRate(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="targetMargin">Target Profit Margin (%)</label>
                <input
                  id="targetMargin"
                  type="number"
                  className="input"
                  placeholder="30"
                  min="0"
                  max="100"
                  value={targetMargin}
                  onChange={(e) => setTargetMargin(e.target.value)}
                />
              </div>
            </div>

            {/* Cost Calculator Toggle */}
            <div className="form-group">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowCalculator(!showCalculator)}
              >
                {showCalculator ? 'Hide' : 'Show'} Cost Calculator
              </button>
            </div>

            {/* Cost Calculator */}
            {showCalculator && (
              <div className="cost-calculator-section">
                <h3>Cost Analysis</h3>
                <p className="help-text">
                  Enter your costs to calculate the optimal price based on your target margin.
                </p>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="directCosts">Direct Costs</label>
                    <input
                      id="directCosts"
                      type="number"
                      className="input"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      value={directCosts}
                      onChange={(e) => setDirectCosts(e.target.value)}
                    />
                    <small>Materials, software, contractors, etc.</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="indirectCosts">Indirect Costs</label>
                    <input
                      id="indirectCosts"
                      type="number"
                      className="input"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      value={indirectCosts}
                      onChange={(e) => setIndirectCosts(e.target.value)}
                    />
                    <small>Overhead, utilities, insurance, etc.</small>
                  </div>
                </div>

                {strategy === PRICING_STRATEGIES.HOURLY && (
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="timeHours">Estimated Time (hours)</label>
                      <input
                        id="timeHours"
                        type="number"
                        className="input"
                        placeholder="0"
                        min="0"
                        step="0.25"
                        value={timeHours}
                        onChange={(e) => setTimeHours(e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="desiredHourlyRate">Desired Hourly Rate</label>
                      <input
                        id="desiredHourlyRate"
                        type="number"
                        className="input"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        value={desiredHourlyRate}
                        onChange={(e) => setDesiredHourlyRate(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {suggestedRate && (
                  <div className="suggested-rate-box">
                    <div className="suggested-label">Suggested Rate (with {targetMargin}% margin):</div>
                    <div className="suggested-value">{formatCurrency(suggestedRate)}</div>
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => setCurrentRate(suggestedRate.toFixed(2))}
                    >
                      Use this rate
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Service'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddServicePricingModal;
